import React, { FC, useRef } from "react";
import LibraryMediaCardIcon from "../../../../../images/media-cards/library.svg";
import UploadMediaCardIcon from "../../../../../images/media-cards/upload.svg";
import DeleteIcon from "../../../../../images/products/trash-white.svg";
import StarIcon from "../../../../../images/products/star-white.svg";
import StarYellowIcon from "../../../../../images/products/star-yellow.svg";
import LibraryIcon from "../../../../../images/common/library-grey-folder.svg";
import UploadIcon from "../../../../../images/common/upload-grey-shape.svg";
import MediaLibraryPopUp from "./MediaLibraryPopUp";
import { MediaTypes } from "../enums";
import { MediaModel } from "../models";
import {
  AuthService,
  CustomLabel,
  FlexContainer,
  LibraryButtons,
  ToastService,
  Video,
} from "@mindmemobile/mm-shared-global";
import {  MediaService } from "../services";
import {
  DescriptionCell,
  ItemFrame,
  ItemImage,
  LabelCell,
  MediaBoxesWrapper,
} from "../styles";
import { MediaStore } from "../store";
import { MediaItem, MediaType } from "@mindmemobile/mm-shared-library";
import { observer } from "mobx-react";
import SharedLibrary from "./SharedLibrary";
import styled from "styled-components";

type IProps = {
  hideLabel?: boolean;
  allowedMediaTypes?: (keyof typeof MediaType)[];
  medias?: MediaModel[];
  //onLibrary: () => void;
  onUpload: (medias: MediaModel[]) => void;
  onDelete: (media: MediaModel) => void;
  multiple: boolean;
  onSelectLibraryMedia: (media: MediaItem) => void;
  onFavourite?: (media: MediaModel) => void;
  showUploadButtons?: boolean;
  hideOverlays?: boolean;
  allowOnlyOne?: boolean;
  toggleLibrary: (show: boolean, fromUpload?: boolean) => void;
  showLibrary: boolean;
  showUpload?: boolean;
  className?: string;
};
const MediaBoxes: FC<IProps> = (props: IProps) => {
  const fileUploadImageRef = useRef<HTMLInputElement>(null);
  const fileUploadBtnRef = useRef<HTMLInputElement>(null);
  const {
    showUploadButtons = true,
    hideOverlays = false,
    allowOnlyOne = false,
  } = props;

  function onUploadMedia(files: FileList | null) {
    if (allowOnlyOne && files && files.length > 1) {
      ToastService.showWarning(
        "You are only allowed to upload a maximum of 1 file"
      );
      return false;
    }
    if (files && files.length > 3) {
      ToastService.showWarning(
        "You are only allowed to upload a maximum of 3 files"
      );
      return false;
    }
    const isImageOnly =
      props.allowedMediaTypes && props.allowedMediaTypes?.length > 0
        ? props.allowedMediaTypes?.includes(MediaTypes.Image) &&
          props.allowedMediaTypes.length === 1
        : false;

    const filteredList = Array.from(files || []).filter((file) => {
      const isImage = file.type.startsWith("image/");
      const isAllowedType = isImageOnly ? isImage : true;
      return file.size / 1048576 <= 5 && isAllowedType;
    });

    if (
      isImageOnly &&
      Array.from(files || []).some((file) => !file.type.startsWith("image/"))
    ) {
      ToastService.showError("Only images are allowed");
      return;
    }

    if (Array.from(files || []).length > 0 && filteredList.length === 0) {
      if (isImageOnly) {
        ToastService.showError("Please upload image files less than 5MB");
        return;
      } else {
        ToastService.showError("Please upload files less than 5MB");
        return;
      }
    }

    MediaService.uploadMedia({
      AccountId: AuthService.authData.accountId,
      Files: filteredList,
    }).subscribe({
      next: (result) => {
        if (result?.error) {
          ToastService.showError(result.error);
          return;
        }
        //const medias = result.medias?.map((m) => MediaItem.deserializeAPIModel(m as IAPIMediaItem)) || [];
        // const nonDuplicateMedias = medias.filter((media) => !isMediaDuplicate(media));

        // if (nonDuplicateMedias.length === 0) {
        //     ToastService.showError('Media is already present.');
        // } else {
        //     props.onUpload(nonDuplicateMedias);
        //     ToastService.showSuccess('Media uploaded successfully');
        // }
      },
      error: () => {
        ToastService.showError(
          "Error in uploading media. Please try again later."
        );
      },
    });
  }

  const uploadClick = () => {
    if (fileUploadImageRef.current) {
      fileUploadImageRef.current.click();
    } else if (fileUploadBtnRef.current) {
      fileUploadBtnRef.current.click();
    }
  };

  const isPageType = (type: string) =>
    type === "MobilePages" || type === "EmailTemplates";

  const renderMedia = (media: MediaModel) => {
    switch (media.type) {
      case MediaTypes.Video:
        return <Video url={media.fullURL} />;
      default:
        return (
          <img
            src={media.fullURL}
            className={`product-image${isPageType(media.type) ? " product-image--page-preview" : ""}`}
          />
        );
    }
  };

  const isMediaDuplicate = (media: MediaItem): boolean => {
    return (
      props.medias?.some(
        (existingMedia) => existingMedia.mediaId === media.id
      ) || false
    );
  };

  const renderLibrary = () => {
    if (!AuthService.mindmeAccountDetails) return;
    const { subAccountId, parentAccountId, userId } =
      AuthService.mindmeAccountDetails;
    return (
      <LibraryMediaWrapper className={props.className}>
        <SharedLibrary
          onMediaItemClick={(media) => {
            props.toggleLibrary(false);
            if (!isMediaDuplicate(media)) {
              props.onSelectLibraryMedia && props.onSelectLibraryMedia(media);
            } else {
              ToastService.showError("Media is already present.");
            }
          }}
          subAccountId={subAccountId}
          parentAccountId={parentAccountId}
          userId={userId}
          bearerToken=""
          showUpload={props.showUpload}
          allowedMediaTypes={props.allowedMediaTypes}
          apiBaseURL={process.env.REACT_APP_MINDME_MAIN_API || ""} // Ensure this is set in your environment variables
        />
      </LibraryMediaWrapper>
    );
  };

  if (props.showLibrary) {
    return <MediaBoxesWrapper>{renderLibrary()}</MediaBoxesWrapper>;
  }
  return (
    <MediaBoxesWrapper className={props.className}>
      <FlexContainer className="label-description-row">
        {!props.hideLabel && (
          <LabelCell>
            <CustomLabel>Media</CustomLabel>
          </LabelCell>
        )}
        <DescriptionCell>
          {props.medias && props.medias.length > 0 ? (
            <>
              {showUploadButtons && (
                <div>
                  <FlexContainer padding="5px 0px 25px" spacing={20}>
                    <LibraryButtons
                      className="afterpay-library-btn"
                      onClick={() => props.toggleLibrary(true, false)}
                    >
                      <img src={LibraryIcon} alt="Library Icon" />
                      Library
                    </LibraryButtons>
                    {false && (
                      <input
                        style={{ display: "none" }}
                        type="file"
                        id="fileUploadButton"
                        multiple={props.multiple}
                        accept="video/*,image/*"
                        ref={fileUploadBtnRef}
                        onChange={(evt) =>
                          onUploadMedia(evt.currentTarget.files)
                        }
                      />
                    )}
                    <LibraryButtons
                      className="afterpay-library-btn ap-upload-btn"
                      // onClick={uploadClick}
                      onClick={() => {
                        props.toggleLibrary(true, true);
                      }}
                    >
                      <img src={UploadIcon} alt="Upload Icon" />
                      Upload
                    </LibraryButtons>
                  </FlexContainer>
                </div>
              )}
              <div>
                <FlexContainer
                  spacing={15}
                  fxWrap="wrap"
                  className="item-frame-wrapper"
                >
                  {props.medias.map((media, idx) => (
                    <ItemFrame key={idx}>
                      <div>
                        <ItemImage
                          className="uploaded-image"
                          bgColor="#fff" // asked by Jeff to remove the bgcolor for item detail page
                        >
                          {renderMedia(media)}
                          {!hideOverlays && (
                            <div className="overlay-product">
                              <FlexContainer
                                alignItems="center"
                                justifyContent="center"
                                className="full-height"
                                spacing={10}
                              >
                                <img
                                  onClick={() => props.onDelete(media)}
                                  src={DeleteIcon}
                                  alt="Delete Icon"
                                  // className="plus-hovered"
                                />
                                {/* <img
                                                                onClick={() => {}}
                                                                src={EditIcon}
                                                                alt="Edit Icon"
                                                                // className="plus-hovered"
                                                            /> */}
                                {media.type !== MediaTypes.Video &&
                                  !media.isFavourite &&
                                  props.onFavourite && (
                                    <img
                                      onClick={() => props.onFavourite?.(media)}
                                      src={StarIcon}
                                      alt="Favourite Icon"
                                    />
                                  )}
                              </FlexContainer>
                            </div>
                          )}
                          {!hideOverlays &&
                            media.isFavourite && ( // media.isFavourite &&
                              <img
                                src={StarYellowIcon}
                                alt="Star Icon"
                                className="favourite-star"
                              />
                            )}
                        </ItemImage>
                      </div>
                    </ItemFrame>
                  ))}
                </FlexContainer>
              </div>
            </>
          ) : (
            <FlexContainer spacing={15} className="media-cards">
              <div>
                <img
                  onClick={() => props.toggleLibrary(true, false)}
                  src={LibraryMediaCardIcon}
                  alt="Library Media Card Icon"
                />
              </div>
              <div>
                {false && (
                  <input
                    style={{ display: "none" }}
                    type="file"
                    id="fileInput"
                    multiple={true}
                    ref={fileUploadImageRef}
                    accept="video/*,image/*"
                    onChange={(evt) => onUploadMedia(evt.currentTarget.files)}
                  />
                )}
                <img
                  // onClick={() => uploadClick()}
                  onClick={() => {
                    props.toggleLibrary(true, true);
                  }}
                  src={UploadMediaCardIcon}
                  alt="Upload Media Card Icon"
                />
              </div>
            </FlexContainer>
          )}
        </DescriptionCell>
      </FlexContainer>
    </MediaBoxesWrapper>
  );
};
export default observer(MediaBoxes);

export const LibraryMediaWrapper = styled.div`
  height: calc(100vh - 50px);
  &.media-box-popup {
    height: calc(100vh - 150px);
  }
  .media-blocks {
    max-width: 100px;
    label {
      word-break: break-word;
      line-height: 16px;
    }
  }
  .library-popup-pagination {
    border: none;
    padding: 0px 20px;
    background-color: #f4f7fb;
    > div:first-child {
      width: 145px;
      text-align: left;
    }
  }
  .media-type-select {
    width: 300px !important;
  }
  .library-image-label {
    line-height: 22px !important;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* Number of lines to display */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &.lib-overlay-payment {
    height: 100%;
    .universal-search-input {
      padding: 6px 10px;
      width: 250px;
      border-radius: 5px;
      border: none;
      outline: none;
      height: 40px;
      &:focus {
        border: none;
        outline: none;
      }
    }
    .universal-single-select {
      padding: 10px;
      border-radius: 5px;
      border: none;
      cursor: pointer;
      width: 140px;
      height: 40px;
      &:focus {
        outline: none;
        border: none;
      }
      select {
        padding: 10px 20px 10px 5px;
      }
      option {
        cursor: pointer;
      }
    }
    .styled-radio,
    .styled-chkbox {
      label,
      input[type="radio"],
      input[type="checkbox"] {
        display: inline-block !important;
      }
    }
    &.pay-lib-popup {
      height: calc(100vh - 150px);
    }
  }
`;
